import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { filter, map, pairwise, withLatestFrom } from 'rxjs/operators';
import { ErrorType, LoginStatusType, PageType } from '@lib/interfaces';
import {
  AswMasterService,
  AswServiceStoreService,
  CommonLibService,
  GetBasicReservationInformationStoreService,
  PageInitService,
  PageLoadingService,
  SystemDateService,
} from '@lib/services';
import { isNumeric } from '@lib/helpers';
import { GetBasicReservationInformationResponseData } from 'src/sdk-amcmember';
import {
  EncryptDutyFreePreOrderRequest,
  EncryptDutyFreePreOrderResponseConvertedString,
  GetOrderRequest,
  GetOrderResponseData,
} from 'src/sdk-servicing';
import { ServicingBaseClass } from '@common/components';
import {
  DutyFreePreorderGuidanceParameter,
  AllOfLinkageParameterExceptUrl,
  LinkageParameterOfSegmentInfo,
  LinkageParameterOfEncrypt,
  UrlActionParam,
  DateFormat,
  LangCode,
} from '@common/interfaces/duty-free-preorder-guidance';
import { GetOrderResultStoreService } from '../get-order-result-store/get-order-result-store.service';
import { RedirectGuidanceService } from '../redirect-guidance.service';
import { EncryptDutyFreePreorderResultStoreService } from './encrypt-duty-free-preorder-result-store.service';
import { ServicingCommonStoreService } from '../servicing-common-store/servicing-common-store.service';
import { ErrorCodeConstants } from '@conf/app.constants';

@Injectable()
export class DutyFreePreorderGuidanceService extends ServicingBaseClass {
  /** 免税品プリオーダー案内パラメータ取得通知イベント */
  notifyParams$ = new Subject<DutyFreePreorderGuidanceParameter>();

  /** 予約基本情報取得APIレスポンス正常通知イベント */
  isGetBasicReservationInformationSuccess$ = new Subject<GetBasicReservationInformationResponseData | void>();

  /** 免税品プリオーダー連携文字列暗号化APIレスポンス異常メッセージコード
   * EBAZ000160: '搭乗者不一致エラー',
   * EBAZ000584: 'PNR情報の取得に失敗',
   * EBAZ000615: '暗号化に失敗',
   */
  private static readonly ApiErrorMessage: { [errorCode: string]: string } = {
    [ErrorCodeConstants.ERROR_CODES.EBAZ000160]: 'E0711',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000584]: 'E0712',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000615]: 'E0713',
  };

  /** キャビンクラスコード */
  private static readonly CabinClass: { [cabin: string]: string } = {
    eco: 'Y',
    ecoPremium: 'E',
    business: 'C',
    first: 'F',
  };

  /** 遷移パターン ("0"(新規予約)) */
  private readonly pattern = '0';

  /** 操作種別 ("0"(戻りなし)) */
  private readonly backKind = '0';

  /** 会社コード ("ANA") */
  private readonly companyCode = 'ANA';

  /** "H"(自宅)、"M"(携帯)、または"B"(会社) */
  private readonly pnrPhonesType = ['H', 'M', 'B'];

  /** PNR Key 下３桁 */
  private readonly lastThreeDigits = '000';

  /** AMC会員番号 */
  private amcNo = '';

  /** PNR情報取得APIレスポンスデータ */
  private pnrData: GetOrderResponseData | undefined;

  /** 免税品プリオーダー連携文字列暗号化APIレスポンスデータ */
  private encryptData: EncryptDutyFreePreOrderResponseConvertedString | undefined;

  constructor(
    private _aswServiceStoreService: AswServiceStoreService,
    private _aswMasterService: AswMasterService,
    private _commonLibService: CommonLibService,
    private _systemDateSvc: SystemDateService,
    private _pageInitService: PageInitService,
    private _pageLoadingService: PageLoadingService,
    private _getOrderResultStoreService: GetOrderResultStoreService,
    private _redirectGuidanceService: RedirectGuidanceService,
    private _getBasicReservationInformationStoreService: GetBasicReservationInformationStoreService,
    private _encryptDutyFreePreorderResultStoreService: EncryptDutyFreePreorderResultStoreService,
    private _servicingCommonStoreService: ServicingCommonStoreService
  ) {
    super();

    this.watchGetOrderInformationSuccess();
    this.watchEncryptDutyFreePreorderSuccess();
    this.watchEncryptDutyFreePreorderFailure();
    this.watchGetBasicReservationInformationSuccess();
    this.watchGetBasicReservationInformationFailure();
  }

  override destroy(): void {}

  /**
   * PNR情報取得APIリクスト
   */
  getParameter(): void {
    const pnrRequest: GetOrderRequest = {
      credential: {
        firstName: this._aswServiceStoreService.aswServiceData.firstName!,
        lastName: this._aswServiceStoreService.aswServiceData.lastName!,
      },
      mask: false,
      skipCheckTasks: true,
      skipWifiInfo: true,
      skipOperationInfo: true,
      skipCprInfo: true,
      skipServicingPapi: true,
    };

    /** サービス共通情報に航空券番号があれば航空券番号を使用してPNR情報取得APIを呼ぶ、
     * 航空券番号がなければ予約番号を使用してPNR情報取得APIを呼ぶ */
    if (this._aswServiceStoreService.aswServiceData.travelDocumentId) {
      pnrRequest.travelDocumentId = this._aswServiceStoreService.aswServiceData.travelDocumentId;
    } else {
      pnrRequest.orderId = this._aswServiceStoreService.aswServiceData.orderId;
    }

    /** PNR情報取得APIを呼び出す */
    this._getOrderResultStoreService.request(pnrRequest);
  }

  /**
   * 免税品プリオーダー連携文字列暗号化APIリクスト
   */
  getEncryptDutyFreePreorderInformation(): void {
    const encryptRequest: EncryptDutyFreePreOrderRequest = {
      credential: {
        firstName: this._aswServiceStoreService.aswServiceData.firstName!,
        lastName: this._aswServiceStoreService.aswServiceData.lastName!,
      },
    };

    /** サービス共通情報に予約番号があれば予約番号を使用して免税品プリオーダー連携文字列暗号化APIを呼ぶ、
     * 予約番号がなければ航空券番号を使用して免税品プリオーダー連携文字列暗号化APIを呼ぶ */
    if (this._aswServiceStoreService.aswServiceData.orderId) {
      encryptRequest.orderId = this._aswServiceStoreService.aswServiceData.orderId;
    } else {
      encryptRequest.travelDocumentId = this._aswServiceStoreService.aswServiceData.travelDocumentId;
    }

    this._encryptDutyFreePreorderResultStoreService.request(encryptRequest);
  }

  /**
   * 外部サイトを表示する
   * @param url (外部サイトURL)
   * @param allOfLinkageParameterExceptUrl (送信用連携パラメータ)
   */
  openUrl(url: string, allOfLinkageParameterExceptUrl: AllOfLinkageParameterExceptUrl): void {
    this._encryptDutyFreePreorderResultStoreService.clear();
    this._getBasicReservationInformationStoreService.resetGetBasicReservationInformation();
    this._getOrderResultStoreService.clear();

    if (this._commonLibService.aswContextStoreService.aswContextData.isAnaApl) {
      // ANAアプリの場合
      this._redirectGuidanceService.anaAplPost(url, allOfLinkageParameterExceptUrl);
    } else {
      this._redirectGuidanceService.post(url, allOfLinkageParameterExceptUrl);
    }
  }

  /**
   * PNR情報取得APIレスポンス正常取得
   */
  private watchGetOrderInformationSuccess(): void {
    this.subscribeService(this._getOrderResultStoreService.success$, (getOrderResultState) => {
      if (getOrderResultState.model?.data?.orderEligibilities?.dutyFreePreorder?.isEligible) {
        /** PNR情報レスポンスデータ */
        this.pnrData = getOrderResultState.model.data;

        /** 免税品プリオーダー連携文字列暗号化APIの呼び出し */
        this.getEncryptDutyFreePreorderInformation();
      } else {
        this._commonLibService.errorsHandlerService.setRetryableError(PageType.PAGE, {
          errorMsgId: 'E0432',
        });

        this._pageInitService.endInit();
      }
    });
  }

  /**
   * 免税品プリオーダー連携文字列暗号化APIレスポンス正常取得
   */
  private watchEncryptDutyFreePreorderSuccess(): void {
    this.subscribeService(this._encryptDutyFreePreorderResultStoreService.success$, (state) => {
      /** 受診したレスポンスデータ */
      this.encryptData = state.model?.convertedString;

      /** 予約基本情報API呼び出し＆画面遷移に必要な値のセット処理 */
      this.getOtherInformation();
    });
  }

  /**
   * 免税品プリオーダー連携文字列暗号化APIレスポンス異常取得
   */
  private watchEncryptDutyFreePreorderFailure(): void {
    this.subscribeService(
      this._encryptDutyFreePreorderResultStoreService.failure$.pipe(
        withLatestFrom(this._commonLibService.apiErrorResponseService.getApiErrorResponse$(), (_, state) => state)
      ),
      (state) => {
        const apiError = state?.errors?.[0];
        if (apiError) {
          const matchErrorMsgId = DutyFreePreorderGuidanceService.ApiErrorMessage[apiError.code];
          if (matchErrorMsgId) {
            this._commonLibService.errorsHandlerService.setNotRetryableError({
              errorType: ErrorType.BUSINESS_LOGIC,
              errorMsgId: matchErrorMsgId,
              apiErrorCode: apiError.code,
            });
          } else {
            this._commonLibService.errorsHandlerService.setNotRetryableError({
              errorType: ErrorType.SYSTEM,
              apiErrorCode: apiError.code,
            });
          }
        }
        this._pageLoadingService.endLoading();
      }
    );
  }

  /**
   * 予約基本情報取得APIレスポンス正常取得
   */
  private watchGetBasicReservationInformationSuccess(): void {
    this.subscribeService(
      this._getBasicReservationInformationStoreService.getGetBasicReservationInformation$().pipe(
        pairwise(),
        filter(([prev, curr]) => prev.isPending === true && curr.isPending === false && curr.isFailure === false),
        map(([_, cur]) => cur)
      ),
      (state) => {
        this.setAmcNoAndGetUrlAndParameter(state.data);
      }
    );
  }

  /**
   * 予約基本情報取得APIレスポンス異常取得
   */
  private watchGetBasicReservationInformationFailure(): void {
    this.subscribeService(
      this._getBasicReservationInformationStoreService.getGetBasicReservationInformation$().pipe(
        pairwise(),
        filter(([prev, curr]) => prev.isPending === true && curr.isPending === false && curr.isFailure === true)
      ),
      () => {
        this.setAmcNoAndGetUrlAndParameter();
      }
    );
  }

  /**
   * 予約基本情報API呼び出し＆画面遷移に必要な値のセット
   */
  private getOtherInformation(): void {
    // リアルログインの場合
    if (this.isRealLogin()) {
      /** 予約基本情報取得API呼び出し */
      this._getBasicReservationInformationStoreService.setGetBasicReservationInformationFromApi({});
    } else {
      /** 連携用URLと全部のパラメーターをセットする */
      this.getUrlAndParameter();
    }
  }

  /**
   * 予約基本情報取得レスポンス.amcNumber(会員番号)を取得＆暗号化APIの呼び出し
   * @param data
   */
  private setAmcNoAndGetUrlAndParameter(data?: GetBasicReservationInformationResponseData) {
    if (data) {
      this.setAmcNo(data);
    }
    /** 連携用URLと全部のパラメーターを取得 */
    this.getUrlAndParameter();
  }

  /**
   * 連携用URLと全部のパラメーターを取得
   */
  private getUrlAndParameter(): void {
    /** 暗号化API：レスポンスデータからパラメータ生成 */
    const linkageParameterOfEncrypt = this.setLinkageParameterOfEncrypt();
    /** PNR情報：レスポンスデータからパラメータ生成 */
    const linkageParameterOfSegmentInfo = this.setLinkageParameterOfSegmentInfo(this.pnrData);
    /** パラメータ：AMC会員番号やその他 */
    const otherPatternFirst = this.setOtherPatternFirst(this.pnrData);
    /** パラメータ：操作種別やその他 */
    const otherPatternSecond = this.setOtherPatternSecond();
    /** パラメータ：遷移パターン */
    const pattern = { pattern: this.pattern };

    /** URL以外の全部のパラメータを結合 */
    const allOfLinkageParameterExceptUrl: AllOfLinkageParameterExceptUrl = Object.assign(
      pattern,
      linkageParameterOfSegmentInfo,
      otherPatternFirst,
      linkageParameterOfEncrypt,
      otherPatternSecond
    );
    /** 遷移用URLを取得 */
    const transitionDestinationUrl = this.getPreorderUrl();
    /** 連携用URLと全部のパラメーター */
    this.notifyParams$.next({
      url: transitionDestinationUrl,
      allOfLinkageParameterExceptUrl: allOfLinkageParameterExceptUrl,
    });

    this._pageInitService.endInit();
  }

  /**
   * 暗号化API：レスポンスデータからパラメータ生成
   * @param responseDate
   * @returns linkageParameterOfEncrypt
   */
  private setLinkageParameterOfEncrypt(): LinkageParameterOfEncrypt {
    /** パラメータ：免税品パラメータ暗号化レスポンス */
    const linkageParameterOfEncrypt: LinkageParameterOfEncrypt = {
      sexCode: this.encryptData?.sexCode || '',
      pnrPaxFirstNameEng: this.encryptData?.pnrPaxFirstNameEng || '',
      pnrPaxLastNameEng: this.encryptData?.pnrPaxLastNameEng || '',
      birthday: this.encryptData?.birthday || '',
      emailAddress: this.encryptData?.emailAddress || '',
    };
    return linkageParameterOfEncrypt;
  }

  /**
   * PNR情報：レスポンスデータからパラメータ生成
   * @param pnrData
   * @returns linkageParameterOfSegmentInfo
   */
  private setLinkageParameterOfSegmentInfo(pnrData?: GetOrderResponseData) {
    /** パラメータ：セグメント情報 */
    const linkageParameterOfSegmentInfo: LinkageParameterOfSegmentInfo = {};

    const flightIds = pnrData?.orderEligibilities?.dutyFreePreorder?.eligibleFlightIds;
    const flightsData = pnrData?.air?.bounds?.filter((data) => data.flights);

    let index = 0;

    flightsData?.forEach((data) => {
      data.flights?.forEach((target) => {
        if (target.id && flightIds?.includes(target.id)) {
          const cabin = target.cabin || '';
          /** 出発日&出発時刻日 */
          const departureDate = this.getDateAsFormat(DateFormat.MMdd, target.departure?.dateTime);
          const departureTime = this.getDateAsFormat(DateFormat.HHmm, target.departure?.dateTime);
          /** 到着日&到着時刻 */
          const arrivalDate = this.getDateAsFormat(DateFormat.MMdd, target.arrival?.dateTime);
          const arrivalTime = this.getDateAsFormat(DateFormat.HHmm, target.arrival?.dateTime);

          const nNo = (index + 1).toString();

          linkageParameterOfSegmentInfo['fltNo' + nNo] = this.getFltNo(
            target.marketingAirlineCode,
            target.marketingFlightNumber
          );
          linkageParameterOfSegmentInfo['compartment' + nNo] = DutyFreePreorderGuidanceService.CabinClass[cabin] || '';
          linkageParameterOfSegmentInfo['booking' + nNo] = target.bookingClass || '';
          linkageParameterOfSegmentInfo['depDate' + nNo] = departureDate;
          linkageParameterOfSegmentInfo['depTime' + nNo] = departureTime;
          linkageParameterOfSegmentInfo['depApo' + nNo] = target.departure?.locationCode || '';
          linkageParameterOfSegmentInfo['arrDate' + nNo] = arrivalDate;
          linkageParameterOfSegmentInfo['arrTime' + nNo] = arrivalTime;
          linkageParameterOfSegmentInfo['arrApo' + nNo] = target.arrival?.locationCode || '';
          index++;
        }
      });
    });

    return linkageParameterOfSegmentInfo;
  }

  /**
   * パラメータ：AMC会員番号やその他
   * @param pnrData
   * @returns otherPatternFirst
   */
  private setOtherPatternFirst(pnrData?: GetOrderResponseData) {
    const orderId = pnrData?.orderId;
    const travelerNumbers = pnrData?.travelersSummary?.travelerNumbers;
    const adt = travelerNumbers?.ADT || 0;
    const b15 = travelerNumbers?.B15 || 0;
    const chd = travelerNumbers?.CHD || 0;
    const ins = travelerNumbers?.INS || 0;
    const inf = travelerNumbers?.INF || 0;
    const isGroupBooking = pnrData?.orderType?.isGroupBooking;
    const pnrPhonesType = pnrData?.contacts?.representative?.phones?.[0]?.type?.toString();
    const pnrPhonesNumber = pnrData?.contacts?.representative?.phones?.[0]?.number?.toString();

    return {
      amcNumber: this.amcNo,
      paxCount: (adt + b15 + chd + ins).toString(),
      infCount: inf.toString(),
      key: this.getPnrKey(orderId, isGroupBooking, pnrPhonesType, pnrPhonesNumber),
    };
  }

  /**
   * パラメータ：操作種別やその他
   * @returns otherPatternSecond
   */
  private setOtherPatternSecond() {
    return {
      backKind: this.backKind,
      companyCode: this.companyCode,
      date: this._systemDateSvc.getFormattedSystemDate(DateFormat.MMddHHmm),
      langCode: this.getLangCodeValue(this._commonLibService.aswContextStoreService.aswContextData.lang),
    };
  }

  /**
   * 免税品プリオーダー(preorder)へ遷移するURLを取得する
   * @returns url
   */
  getPreorderUrl(): string {
    /** Akamaiからプロパティ(カテゴリ:その他アプリケーション設定)キー:”preorder.url”(免税品プリオーダーURL)に対する値を取得 */
    const preorderUrl = this._aswMasterService.getMPropertyByKey('otherApplicationSettings', 'preorder.url');
    /** 操作日時*/
    const systemDate = this._systemDateSvc.getFormattedSystemDate(DateFormat.yyyyMMddHHmmss);
    /** 遷移先種別 */
    const actionParamLangCode = this.getUrlActionParamValue();

    return `${preorderUrl}rand=${systemDate}&action_param=${actionParamLangCode}`;
  }

  /**
   * UrlActionParamの値をセット
   *
   * @param langCode
   * @returns UrlActionParam
   */
  private getUrlActionParamValue(): UrlActionParam {
    if (this._commonLibService.aswContextStoreService.aswContextData.lang === LangCode.JA) {
      return UrlActionParam.jaActionParam;
    } else if (this._commonLibService.aswContextStoreService.aswContextData.lang === LangCode.EN) {
      return UrlActionParam.otherActionParam;
    } else {
      return this._servicingCommonStoreService.servicingCommon.lang === LangCode.JA
        ? UrlActionParam.jaActionParam
        : UrlActionParam.otherActionParam;
    }
  }

  /**
   * loginStatusからリアルログイン状態かどうかを取得する
   *
   * @param loginStatus
   * @returns boolean
   */
  private isRealLogin(): boolean {
    // ログイン状態の取得
    const loginStatus = this._commonLibService.aswContextStoreService.aswContextData.loginStatus;

    return loginStatus === LoginStatusType.REAL_LOGIN;
  }

  /**
   * 予約基本情報取得結果を元にAMC会員番号の値を設定する
   * @param data 予約基本情報取得結果
   */
  private setAmcNo(data: GetBasicReservationInformationResponseData): void {
    this.amcNo = data.amcNumber || '';
  }

  /**
   * 言語情報の判定結果取得
   */
  get isJaOrEn(): boolean {
    /** ユーザ共通情報.言語情報を取得 */
    const lang = this._commonLibService.aswContextStoreService.aswContextData.lang;

    /** 言語情報の判定 */
    return Object.values<string>(LangCode).includes(lang);
  }

  /**
   * fltNo(便名)の値をセット
   * セグメント情報.marketingAirlineCodeと、セグメント情報.marketingFlightNumberを連結した値。
   * (当該セグメント情報. marketingFlightNumberが4桁未満の場合は前に0埋めて4桁にする)
   *
   * @param marketingAirlineCode
   * @param marketingFlightNumber
   * @returns fltNo
   */
  private getFltNo(marketingAirlineCode?: string, marketingFlightNumber?: string): string {
    if (marketingAirlineCode === undefined || marketingFlightNumber === undefined) {
      return '';
    }

    let mfn;
    if (marketingFlightNumber.length < 4) {
      mfn = this.zeroPadding(marketingFlightNumber, 4);
    } else {
      mfn = marketingFlightNumber;
    }
    return marketingAirlineCode + mfn;
  }

  /**
   * 桁数埋め処理
   * 指定桁数になるように前0埋め
   * @param num (Target)
   * @param len (桁数)
   * @returns zeroPaddingString
   */
  private zeroPadding(num: string, len: number): string {
    return ('0'.repeat(len) + num).slice(-len);
  }

  /**
   * PNRキーの値をセット
   *
   * @param rsvNo (PNR情報.data.予約番号)
   * @param isGrpBooking (PNR情報.data.orderType.isGroupBooking)
   * @param phonesType (PNR情報.contacts.representative.phones.type)
   * @param phonesNo (PNR情報.contacts.representative.phones.number)
   * @returns PnrKey
   */
  private getPnrKey(rsvNo?: string, isGrpBooking?: boolean, phonesType?: string, phonesNo?: string) {
    const conditionsMet =
      phonesType &&
      phonesNo &&
      isGrpBooking !== undefined &&
      !isGrpBooking &&
      this.pnrPhonesType.includes(phonesType) &&
      phonesNo.length > 3 &&
      isNumeric(phonesNo);

    return conditionsMet ? rsvNo + phonesNo.slice(-3) : rsvNo + this.lastThreeDigits;
  }

  /**
   * LangCodeの値をセット
   *
   * @param langCode
   * @returns LangCode
   */
  getLangCodeValue(langCode: string) {
    if (langCode === LangCode.JA) {
      return LangCode.JA;
    }
    return LangCode.EN;
  }

  /**
   * 日付のフォーマット変換
   * @param originalDate
   * @param dateFormat
   * @returns formatted date or time
   */
  private getDateAsFormat(dateFormat: DateFormat, originalDate?: string) {
    if (originalDate === undefined) {
      return '';
    }

    const dateTimeList = originalDate.split('T');
    const date = dateTimeList[0].split('-');
    const time = dateTimeList[1].split(':');
    const month = date[1];
    const day = date[2];
    const hour = time[0];
    const minutes = time[1];

    if (dateFormat === DateFormat.MMdd) {
      return `${month}${day}`;
    } else if (dateFormat === DateFormat.HHmm) {
      return `${hour}${minutes}`;
    } else {
      return originalDate;
    }
  }
}
